import React from 'react';
import PageSeo from '../components/PageSeo';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BrandHeroEyebrow from '../components/BrandHeroEyebrow';
import { brandHeroSection, heroContentZ } from '../theme/heroStyles';

const AboutPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const values = [
    {
      icon: <BusinessIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Excellence',
      description: 'We strive for excellence in everything we do, delivering the highest quality solutions to our clients.'
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Integrity',
      description: 'We conduct business with honesty, transparency, and ethical practices that build lasting trust.'
    },
    {
      icon: <EmojiEventsIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Innovation',
      description: 'We embrace new technologies and creative solutions to solve complex business challenges.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Partnership',
      description: 'We work collaboratively with our clients, building long-term relationships based on mutual success.'
    }
  ];

  const team = [
    {
      name: 'Diesel City Team',
      role: 'Expert Technicians',
      image: '/assets/IMG-20250819-WA0056.jpg',
      description: 'Our team of certified technicians with decades of experience in diesel fuel injection technology.'
    },
    {
      name: 'RMI Certified',
      role: 'Industry Standards',
      image: '/assets/IMG-20250819-WA0049.jpg',
      description: 'Member of the Retail Motor Industry and affiliated to ARA (Automotive Remanufacture Association).'
    },
    {
      name: 'Quality Assurance',
      role: 'Six months on labour and new parts fitted',
      image: '/assets/IMG-20250819-WA0011.jpg',
      description: 'Six months on labour and new parts fitted.'
    },
    {
      name: 'State-of-the-Art',
      role: 'Modern Equipment',
      image: '/assets/State of the art .jpeg',
      description: 'Equipped with the latest diagnostic and testing equipment for all diesel systems.'
    }
  ];

  const achievements = [
    'Established since 1969',
    'RMI & ARA certified member',
    'Six months warranty on labour and new parts fitted',
    'State-of-the-art diagnostic equipment',
    'Clean Room for Common Rail systems',
    'All makes of vehicles and machinery'
  ];

  return (
    <>
      <PageSeo
        pathname="/about"
        title="About Us | Diesel City | Johannesburg Diesel Specialists"
        description="Diesel City since 1969: RMI and ARA-affiliated diesel fuel injection workshop in Selby, Johannesburg. Certified technicians, warranty on labour and new parts."
        ogTitle="About Diesel City"
        ogDescription="Our history, certifications and team — diesel injection excellence in Johannesburg since 1969."
      />

      <Box
        sx={{
          ...brandHeroSection(theme, '/assets/IMG-20250819-WA0049.jpg'),
        }}
      >
        <Container maxWidth="lg" sx={heroContentZ}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <BrandHeroEyebrow label="About Diesel City" />
              </Box>
              <Typography
                variant={isMobile ? 'h4' : 'h2'}
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2,
                  mb: 2,
                  fontSize: { xs: '1.45rem', md: '1.95rem' },
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                Maintaining the Wheels in
                <Box component="span" sx={{
                  color: 'secondary.main',
                  display: 'block',
                  mt: 1
                }}>
                  Motion
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  opacity: 0.95,
                  lineHeight: 1.7,
                  maxWidth: 500
                }}
              >
                To ensure that progress continues smoothly and efficiently, it is essential to maintain momentum across all ongoing activities. This requires a commitment to regular monitoring of processes, swift response to any obstacles that may arise, and ongoing adjustments to workflows. Such vigilance ensures that operations do not stall, and that objectives are consistently achieved within the planned timeframes.
              </Typography>
              <Typography
                variant={isMobile ? 'h5' : 'h3'}
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2,
                  mb: 2,
                  fontSize: { xs: '1.25rem', md: '1.6rem' },
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                Commitment to Service
                <Box component="span" sx={{
                  color: 'secondary.main',
                  display: 'block',
                  mt: 1
                }}>
                  Excellence
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 0,
                  opacity: 0.95,
                  lineHeight: 1.7,
                  maxWidth: 500
                }}
              >
                Our team is composed of dedicated, passionate professionals who strive for excellence in every aspect of service delivery. Through collaboration and a shared commitment to our objectives, we maintain a high standard of performance and ensure that the wheels remain in motion, driving the organisation towards sustained achievement.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: { xs: 300, md: 500 },
                  position: 'relative'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    maxWidth: 500,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    border: '4px solid rgba(255,255,255,0.2)'
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/IMG-20250819-WA0049.jpg"
                    alt="Diesel City Team"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: 2,
                      width: 'auto',
                      height: 'auto'
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Mission Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: '100%',
                  height: 500,
                  backgroundColor: 'background.default',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}
              >
                <Box
                  component="img"
                  src="/assets/machineReplace2.jpeg"
                  alt="Our Mission"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: 2,
                    width: 'auto',
                    height: 'auto'
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: { xs: '1.5rem', md: '1.85rem' },
                }}
              >
                Our Mission
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.7,
                  mb: 3
                }}
              >
                We pride ourselves in the highest standards of quality in every job we undertake.
                We are dedicated to delivering work that meets stringent quality expectations,
                ensuring the best possible outcomes for our customers.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.7,
                  mb: 3
                }}
              >
                We strive to provide timeous and cost effective repair solutions for all our customers.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.7,
                  mb: 4
                }}
              >
                Our workflow is designed to minimise delays and keep the wheels turning.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.7,
                  mb: 3
                }}
              >
                We are a team of passionate professionals dedicated to service excellence and quick turnaround times.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.7,
                  mb: 3
                }}
              >
                We are fully equipped to handle the high pressure demands of today's common rail pumps, always ensuring they meet the manufacturers specifications.  We are equipped with the latest diagnostic and testing equipment to accurately service and repair diesel pumps and injectors.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.7,
                  mb: 2,
                  fontWeight: 600
                }}
              >
                We service all major brands, including:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.7
                }}
              >
                Bosch  -  Zexel  -  Delphi  -  Denso  -  Diesel Kikki  -  Yanmar  -  Cummins
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Values Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: { xs: '1.5rem', md: '1.85rem' }
              }}
            >
              Our Core Values
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: 700,
                mx: 'auto',
                fontSize: { xs: '0.9375rem', md: '1.0625rem' },
              }}
            >
              The principles that guide everything we do
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 4,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
                      transition: 'all 0.3s ease-in-out',
                    },
                    transition: 'all 0.3s ease-in-out'
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{ mb: 3 }}>
                      {value.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        fontSize: { xs: '1rem', md: '1.1rem' },
                      }}
                    >
                      {value.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.6,
                        fontSize: { xs: '0.875rem', md: '1rem' }
                      }}
                    >
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Team Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: { xs: '1.5rem', md: '1.85rem' }
              }}
            >
              Meet Our Team
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: 700,
                mx: 'auto',
                fontSize: { xs: '0.9375rem', md: '1.0625rem' },
              }}
            >
              The experts behind our success
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {team.map((member, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
                      transition: 'all 0.3s ease-in-out',
                    },
                    transition: 'all 0.3s ease-in-out'
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: 300,
                      backgroundColor: 'background.default',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2
                    }}
                  >
                    <Box
                      component="img"
                      src={member.image}
                      alt={member.name}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        width: 'auto',
                        height: 'auto'
                      }}
                    />
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1rem', md: '1.1rem' },
                      }}
                    >
                      {member.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="primary.main"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        fontSize: { xs: '0.875rem', md: '1rem' }
                      }}
                    >
                      {member.role}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.6,
                        fontSize: { xs: '0.875rem', md: '1rem' }
                      }}
                    >
                      {member.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Achievements Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: { xs: '1.5rem', md: '1.85rem' }
                }}
              >
                Our Achievements
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  mb: 4,
                  lineHeight: 1.6,
                  fontSize: { xs: '0.9375rem', md: '1.0625rem' },
                }}
              >
                Milestones that define our journey of excellence
              </Typography>
              <Stack spacing={2}>
                {achievements.map((achievement, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      {achievement}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: '100%',
                  height: 500,
                  backgroundColor: 'background.default',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}
              >
                <Box
                  component="img"
                  src="/assets/IMG-20250819-WA0046.jpg"
                  alt="Our Achievements"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: 2,
                    width: 'auto',
                    height: 'auto'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default AboutPage;
